import { useNavigate } from 'react-router-dom';

import { resetPasswordSchema } from '../schema/resetPassword.schema';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useCaptchaDataQuery,
  useRequestResetPasswordMutation,
} from '@common/cache';
import { InferType } from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

type IFormShape = InferType<typeof resetPasswordSchema>;

const initialValues = {
  email: '',
  captcha_answer: '',
};

interface UseResetPasswordProps {
  validateEmail: (data: {
    email: string;
    captcha_id: string;
    captcha_answer: string;
  }) => void;
}

export const useResetPassword = ({ validateEmail }: UseResetPasswordProps) => {
  const navigate = useNavigate();
  const {
    data = { id: '', svg: '' },
    refetch,
    isFetching,
  } = useCaptchaDataQuery();

  const mutation = useRequestResetPasswordMutation();

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(resetPasswordSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
  } = formMethods;

  const submitHandler = async (values: IFormShape) => {
    try {
      const { email, captcha_answer } = values;

      await mutation.mutateAsync({ ...values, captcha_id: data.id });

      validateEmail({
        email,
        captcha_id: data.id,
        captcha_answer,
      });
      reset({ email, captcha_answer });
      toast.success('Email successfully sent');
    } catch (error) {
      toast.error(formatApiError(error));
      setValue('captcha_answer', '');
    } finally {
      refetch();
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleReturnToLogin = () => {
    navigate('/auth/login');
  };

  const handleRefresh = () => {
    refetch();
  };

  return {
    isSubmitting,
    handleReturnToLogin,
    svg: data.svg,
    handleRefresh,
    loading: isFetching,
    formMethods,
    onSubmit,
  };
};
